import React, { useState } from "react";
import useAnnouncement from "./useAnnouncement";
import { format } from "date-fns";
import ConfirmationModal from "./ConfirmationModal";

function AnnouncementsList({ onEdit, onCreateNew }) {
  const [page, setPage] = useState(1);
  const {
    announcements,
    isGettingAnnouncements,
    deleteAnnouncement,
    isDeletingAnnouncement,
    page: currentPage,
    totalPages,
    limit,
    total,
  } = useAnnouncement({ page, limit: 10 });
  const [deleteModal, setDeleteModal] = useState({
    open: false,
    announcementId: null,
    announcementTitle: null,
  });

  // console.log(
  //   "announcements: ",
  //   announcements,
  //   " ",
  //   "current page: ",
  //   announcements?.data?.pagination?.currentPage,
  //   " total pages: ",
  //   announcements?.data?.pagination?.totalPages
  // );

  //   announcements.data.announcements;
  const handleDelete = (announcementId, announcementTitle) => {
    setDeleteModal({ open: true, announcementId, announcementTitle });
  };

  const confirmDelete = () => {
    if (deleteModal.announcementId) {
      deleteAnnouncement(deleteModal.announcementId);
      setDeleteModal({
        open: false,
        announcementId: null,
        announcementTitle: null,
      });
    }
  };

  const cancelDelete = () => {
    setDeleteModal({
      open: false,
      announcementId: null,
      announcementTitle: null,
    });
  };

  const getStatusBadge = (isSent) => {
    return isSent ? (
      <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
        Sent
      </span>
    ) : (
      <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
        Pending
      </span>
    );
  };

  const getTargetTypeBadge = (targetType) => {
    const config = {
      all: { color: "bg-blue-100 text-blue-800", text: "All Users" },
      users: { color: "bg-green-100 text-green-800", text: "Users Only" },
      admins: { color: "bg-purple-100 text-purple-800", text: "Admins Only" },
    };
    const { color, text } = config[targetType] || config.all;
    return (
      <span className={`px-2 py-1 text-xs font-medium ${color} rounded-full`}>
        {text}
      </span>
    );
  };

  if (isGettingAnnouncements) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (
    !announcements?.data?.announcements ||
    announcements?.data?.announcements?.length === 0
  ) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-500 text-lg mb-4">No announcements found</div>
        <div className="text-gray-400 text-sm">
          Create your first announcement to get started
        </div>
      </div>
    );
  }

  // Pagination controls
  const handlePrev = () => setPage((p) => Math.max(1, p - 1));
  const handleNext = () => setPage((p) => Math.min(totalPages, p + 1));

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">Announcements</h2>
        <button
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors"
          onClick={onCreateNew}
        >
          + New Announcement
        </button>
      </div>
      <div className="grid gap-4">
        {announcements.data.announcements?.map((announcement) => (
          <div
            key={announcement._id}
            className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow"
          >
            <div className="flex justify-between items-start mb-4">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {announcement.title}
                </h3>
                <p className="text-gray-600 mb-3 line-clamp-3">
                  {announcement.body}
                </p>
                <div className="flex flex-wrap gap-2 mb-3">
                  {getStatusBadge(announcement.isSent)}
                  {getTargetTypeBadge(announcement.targetType)}
                  {announcement.deliveryMethods?.useFcm && (
                    <span className="px-2 py-1 text-xs font-medium bg-orange-100 text-orange-800 rounded-full">
                      FCM
                    </span>
                  )}
                </div>
                <div className="text-sm text-gray-500">
                  Scheduled:{" "}
                  {format(new Date(announcement.scheduledDate), "PPP 'at' p")}
                </div>
              </div>
              <div className="flex gap-2 ml-4">
                {/* <button
                  onClick={() => onEdit(announcement)}
                  className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                >
                  Edit
                </button> */}
                <button
                  onClick={() =>
                    handleDelete(announcement._id, announcement.title)
                  }
                  disabled={isDeletingAnnouncement}
                  className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {isDeletingAnnouncement ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Pagination Controls */}
      <div className="flex justify-between items-center mt-8">
        <button
          onClick={handlePrev}
          disabled={page === 1}
          className="px-4 py-2 rounded bg-gray-200 text-gray-800 hover:bg-gray-300 disabled:opacity-50"
        >
          Previous
        </button>
        <span className="text-gray-700">
          Page {announcements?.data?.pagination?.currentPage} of{" "}
          {announcements?.data?.pagination?.totalPages} (
          {announcements?.data?.pagination?.totalPages} total)
        </span>
        <button
          onClick={handleNext}
          disabled={page === totalPages}
          className="px-4 py-2 rounded bg-gray-200 text-gray-800 hover:bg-gray-300 disabled:opacity-50"
        >
          Next
        </button>
      </div>
      {/* Confirmation Modal */}
      <ConfirmationModal
        open={deleteModal.open}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        announcementTitle={deleteModal.announcementTitle}
        loading={isDeletingAnnouncement}
        error={null}
      />
    </div>
  );
}

export default AnnouncementsList;
